import { useCallback, useEffect, useRef, useState } from "react";
import { useOrgContext } from "@components/layout/OrgContext";
import { getTriageRules, updateTriageRule, type TriageRule } from "../lib/auth";

export function useTriageRules() {
  const { currentMembership, currentOrg, isSwitching } = useOrgContext();
  const orgId = currentOrg?.id;
  const requestGeneration = useRef(0);
  const [loadedOrgId, setLoadedOrgId] = useState<string | undefined>();
  const canEditRules = !isSwitching && ["owner", "admin"].includes(currentMembership?.role ?? "");
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updatingRuleId, setUpdatingRuleId] = useState<string | null>(null);
  const [rules, setRules] = useState<TriageRule[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadRules = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await getTriageRules();
      if (generation !== requestGeneration.current) return;
      setRules(data);
      setLoadedOrgId(orgId);
    } catch (error) {
      if (generation !== requestGeneration.current) return;
      setLoadError(error instanceof Error ? error.message : "Unable to load triage rules.");
    } finally {
      if (generation === requestGeneration.current) setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    setRules([]);
    setLoadError(null);
    setUpdateError(null);
    void loadRules();
    return () => { requestGeneration.current += 1; };
  }, [loadRules]);

  const visibleRules = loadedOrgId === orgId && !isSwitching ? rules : [];

  const toggleRule = async (rule: TriageRule) => {
    if (!canEditRules || updatingRuleId) return;
    setUpdateError(null);
    setUpdatingRuleId(rule.id);
    try {
      await updateTriageRule(rule.id, { enabled: !rule.enabled });
      await loadRules();
    } catch (error) {
      setUpdateError(error instanceof Error ? error.message : "Unable to update triage rule.");
    } finally {
      setUpdatingRuleId(null);
    }
  };

  return {
    orgId,
    canEditRules,
    rules: visibleRules,
    loadError,
    isLoading,
    updateError,
    updatingRuleId,
    reloadRules: loadRules,
    toggleRule,
  };
}
